/**
 * Which edges of the matrix's scroll box currently cut content off
 * (2026-08-24 grill, point 1).
 *
 * The frame used to be a fixed `rounded-lg border` on an `h-full` box, so it
 * always drew itself at the viewport rectangle regardless of the table: dead
 * space inside the border when the matrix was small, and a tidy rounded box
 * when it was actually clipped mid-scroll — the border claimed "this is the
 * whole table" in both cases, which is exactly what it could not know.
 *
 * Now the box hugs the table, and each edge you can still scroll past **loses
 * its border line** and the rounding on its two adjacent corners. The frame
 * therefore reads as open on precisely the sides where more content exists,
 * and closed only where the table genuinely ends. Applied on all four edges,
 * including the two backed by the sticky header row / label column (grill
 * decision — symmetry was preferred over hiding the effect there).
 *
 * The pure geometry lives here rather than inline in `matrix-grid.tsx` so it
 * is unit-testable without mounting the grid, matching how `zoom.ts` and
 * `keyboard-shortcuts.ts` already split their logic out.
 */

/** The subset of an element's scroll geometry this module needs. */
export interface ScrollMetrics {
  scrollTop: number;
  scrollLeft: number;
  scrollWidth: number;
  scrollHeight: number;
  clientWidth: number;
  clientHeight: number;
}

export interface ClippedEdges {
  top: boolean;
  right: boolean;
  bottom: boolean;
  left: boolean;
}

/** Nothing clipped — the initial state, and the state of a table that fits. */
export const NO_CLIPPED_EDGES: ClippedEdges = {
  top: false,
  right: false,
  bottom: false,
  left: false,
};

/**
 * Sub-pixel slack, in CSS px. Fractional layout (a zoom step landing on a
 * non-integer line-height, a `w-max` table whose columns sum to x.5px) routinely
 * leaves `scrollWidth` a fraction above `clientWidth` with nothing actually
 * hidden. Without this the frame would flicker an "open" edge on a table that
 * visibly fits.
 */
const EPSILON_PX = 1;

export function clippedEdges(metrics: ScrollMetrics): ClippedEdges {
  const maxScrollLeft = metrics.scrollWidth - metrics.clientWidth;
  const maxScrollTop = metrics.scrollHeight - metrics.clientHeight;
  return {
    top: metrics.scrollTop > EPSILON_PX,
    left: metrics.scrollLeft > EPSILON_PX,
    right: metrics.scrollLeft < maxScrollLeft - EPSILON_PX,
    bottom: metrics.scrollTop < maxScrollTop - EPSILON_PX,
  };
}

/** True when both `ClippedEdges` describe the same state — lets the grid skip no-op re-renders on every scroll event. */
export function sameClippedEdges(a: ClippedEdges, b: ClippedEdges): boolean {
  return a.top === b.top && a.right === b.right && a.bottom === b.bottom && a.left === b.left;
}

/**
 * Tailwind classes for the frame. Border sides are listed individually (never
 * the shorthand `border`) so a clipped side can simply be omitted, and each
 * corner is rounded only when **both** of its adjacent edges are unclipped.
 */
export function clippedEdgeClasses(edges: ClippedEdges): string {
  return [
    'border-gray-200',
    edges.top ? null : 'border-t',
    edges.right ? null : 'border-r',
    edges.bottom ? null : 'border-b',
    edges.left ? null : 'border-l',
    !edges.top && !edges.left ? 'rounded-tl-lg' : null,
    !edges.top && !edges.right ? 'rounded-tr-lg' : null,
    !edges.bottom && !edges.right ? 'rounded-br-lg' : null,
    !edges.bottom && !edges.left ? 'rounded-bl-lg' : null,
  ]
    .filter((entry): entry is string => entry !== null)
    .join(' ');
}
